import { Lock, ArrowLeft, Eye, EyeOff, Hash, LayoutDashboard, Loader2, Mail, ShieldCheck, Sun, Moon } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { fetchUsers, sendOTPToEmail } from '../services/dataService';
import { User } from '../types';

interface LoginProps {
  onLoginSuccess: (user: User) => void;
  theme?: 'dark' | 'light';
  onToggleTheme?: () => void;
}

type LoginStep = 'credentials' | 'otp';

const decodeJwt = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (e) { return null; }
};

const generateAlphanumericCode = (length: number = 6) => {
  const letters = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const numbers = '23456789';
  const allChars = letters + numbers;
  let result = '';
  result += letters.charAt(Math.floor(Math.random() * letters.length));
  result += numbers.charAt(Math.floor(Math.random() * numbers.length));
  for (let i = 0; i < length - 2; i++) {
    result += allChars.charAt(Math.floor(Math.random() * allChars.length));
  }
  return result.split('').sort(() => 0.5 - Math.random()).join('');
};

const Login: React.FC<LoginProps> = ({ onLoginSuccess, theme = 'light', onToggleTheme }) => {
  const [step, setStep] = useState<LoginStep>('credentials');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [otpInput, setOtpInput] = useState('');
  const [generatedOtp, setGeneratedOtp] = useState('');
  const [otpCreatedAt, setOtpCreatedAt] = useState<number | null>(null);
  const [timeLeft, setTimeLeft] = useState<number>(0); 
  const [validatedUser, setValidatedUser] = useState<User | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);

  const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
  const ICON_LOGO_ID = import.meta.env.VITE_LOGO_ICON_ID || '';
  const TEXT_LOGO_ID = theme === 'dark' ? (import.meta.env.VITE_LOGO_TEXT_DARK_ID || '') : (import.meta.env.VITE_LOGO_TEXT_LIGHT_ID || '');

  useEffect(() => { setImageError(false); }, [theme]);

  useEffect(() => {
    if (step === 'otp' && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (step === 'otp' && timeLeft === 0) {
      setError("Código expirado. Por favor, genere uno nuevo.");
    }
  }, [step, timeLeft]);

  const handleGoogleResponse = useCallback(async (response: any) => {
    setIsLoading(true);
    setError(null);
    try {
      const payload = decodeJwt(response.credential);
      if (!payload || !payload.email) throw new Error("No se pudo obtener el email");
      const users = await fetchUsers();
      const foundUser = users.find(u => u.username.trim().toLowerCase() === payload.email.trim().toLowerCase());
      if (foundUser) {
        if (foundUser.status?.toUpperCase() === 'INACTIVO') {
          setError("Esta cuenta de Google está desactivada.");
        } else {
          onLoginSuccess(foundUser);
        }
      } else {
        setError(`El email ${payload.email} no está registrado.`);
      }
    } catch (err) { setError("Error al conectar con Google."); } 
    finally { setIsLoading(false); }
  }, [onLoginSuccess]);

  useEffect(() => {
    const initGoogle = () => {
      if (typeof (window as any).google !== 'undefined' && GOOGLE_CLIENT_ID && step === 'credentials') {
        (window as any).google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleResponse,
          auto_select: false
        });
        const btn = document.getElementById("googleBtn");
        if (btn) {
            // Calculate width based on container, ensuring it stays within Google's allowed range (200-400)
            // and fits within the parent container
            const containerWidth = btn.getBoundingClientRect().width;
            let targetWidth = containerWidth > 0 ? containerWidth : 350;
            if (targetWidth > 400) targetWidth = 400;
            
            (window as any).google.accounts.id.renderButton(
              btn,
              { 
                theme: "outline", // Force white background
                size: "large", 
                width: Math.floor(targetWidth).toString(), 
                shape: "pill", 
                text: "signin_with",
                logo_alignment: "left"
              }
            );
        }
      }
    };
    const timer = setTimeout(initGoogle, 1000);
    return () => clearTimeout(timer);
  }, [GOOGLE_CLIENT_ID, theme, handleGoogleResponse, step]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      const users = await fetchUsers();
      const foundUser = users.find(u => 
        u.username.trim().toLowerCase() === username.trim().toLowerCase() && 
        String(u.password).trim() === String(password).trim()
      );
      
      if (foundUser) {
        if (foundUser.status?.toUpperCase() === 'INACTIVO') {
            setError("Cuenta desactivada temporalmente.");
            setIsLoading(false);
        } else {
            const otp = generateAlphanumericCode(6);
            setGeneratedOtp(otp);
            setOtpCreatedAt(Date.now());
            setTimeLeft(300);
            setValidatedUser(foundUser);
            sendOTPToEmail(foundUser.username, otp).catch(err => console.error("Background OTP send failed", err));
            setStep('otp');
            setIsLoading(false);
        }
      } else { 
        setError("Usuario o contraseña incorrectos."); 
        setIsLoading(false);
      }
    } catch (err) { 
      setError("Error de conexión con el servidor."); 
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpCreatedAt) return;
    const now = Date.now();
    if ((now - otpCreatedAt) > (5 * 60 * 1000)) { setError("El código ha expirado."); return; }
    if (otpInput.toUpperCase().trim() === generatedOtp.toUpperCase() && validatedUser) {
        onLoginSuccess(validatedUser);
    } else { setError("El código introducido es incorrecto."); }
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const bgGradient = theme === 'dark' ? 'bg-[#0f0f0f]' : 'bg-slate-50';
  const cardBg = theme === 'dark' ? 'bg-[#161616]/80 border-[#262626]' : 'bg-white/80 border-white';
  const textMain = theme === 'dark' ? 'text-white' : 'text-slate-900';
  const textMuted = theme === 'dark' ? 'text-slate-500' : 'text-slate-400';
  const inputBg = theme === 'dark' ? 'bg-black/40 border-white/10 text-white focus:border-indigo-500/50' : 'bg-white border-slate-200 text-slate-800 focus:border-indigo-500/50';

  return (
    <div className={`h-screen w-full ${bgGradient} flex flex-col items-center overflow-y-auto overflow-x-hidden transition-colors duration-500 custom-scrollbar`}>
      <div className={`fixed top-[-10%] left-[-10%] w-[100%] h-[70%] rounded-full blur-[100px] pointer-events-none opacity-20 animate-blob ${theme === 'dark' ? 'bg-indigo-900' : 'bg-indigo-200'}`}></div>
      <div className={`fixed bottom-[-10%] right-[-10%] w-[100%] h-[60%] rounded-full blur-[100px] pointer-events-none opacity-20 animate-blob animation-delay-4000 ${theme === 'dark' ? 'bg-emerald-800' : 'bg-teal-200'}`}></div>
      <button onClick={onToggleTheme} className={`fixed top-4 right-4 p-2.5 rounded-full transition-all duration-300 shadow-xl active:scale-95 z-50 ${theme === 'dark' ? 'bg-white/10 text-yellow-400 hover:bg-white/20' : 'bg-white text-indigo-600 hover:bg-slate-50 border border-slate-100'}`}><Sun size={18} /></button>
      <div className="flex-1 w-full max-w-[420px] flex flex-col items-center justify-center p-4 sm:p-6 z-10 space-y-3 sm:space-y-4">
        <div className="text-center w-full animate-in fade-in slide-in-from-top-4 duration-700 flex flex-col items-center">
            <div className={`relative mb-1 transition-all duration-500`}>
                <div className={`absolute inset-0 blur-2xl opacity-40 scale-150 ${theme === 'dark' ? 'bg-indigo-500/30' : 'bg-indigo-400/20'}`}></div>
                <div className={`relative w-16 h-16 sm:w-24 sm:h-24 flex items-center justify-center transition-all duration-500 hover:scale-110 cursor-default overflow-visible`}>
                    <img src={`https://drive.google.com/thumbnail?id=${ICON_LOGO_ID}&sz=w500`} alt="Logo" className="w-full h-full object-contain drop-shadow-2xl" />
                </div>
            </div>
            <div className={`flex justify-center ${theme === 'dark' ? 'h-10 sm:h-12' : 'h-9 sm:h-10'} items-center overflow-hidden`}>
                {!imageError ? (<img src={`https://drive.google.com/thumbnail?id=${TEXT_LOGO_ID}&sz=w1000`} alt="ADVANCE" className={`max-w-full max-h-full object-contain transition-all duration-500`} onError={() => setImageError(true)} />) : (<h1 className={`text-xl sm:text-2xl font-black uppercase tracking-[0.2em] ${textMain}`}>ADVANCE</h1>)}
            </div>
            <p className={`text-[7px] sm:text-[9px] font-black uppercase tracking-[0.3em] mt-1 ${textMuted}`}>&nbsp;</p>
        </div>
        <div className={`w-full backdrop-blur-3xl border p-5 sm:p-8 rounded-[2rem] shadow-2xl transition-all duration-500 ${cardBg} max-h-[75vh] flex flex-col`}>
            <div className="flex-1 overflow-y-auto custom-scrollbar pr-1">
                {step === 'credentials' ? (
                    <div className="animate-in fade-in zoom-in-95 duration-300">
                        <form onSubmit={handleLogin} className="space-y-3 sm:space-y-4">
                            <div className="space-y-1.5">
                                <label className={`text-[9.5px] sm:text-[10.5px] font-black uppercase ml-1 ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600'}`}>Correo de Usuario</label>
                                <div className="relative group">
                                    <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-indigo-500 transition-colors" />
                                    <input type="email" value={username} onChange={e => setUsername(e.target.value)} className={`w-full border rounded-xl py-2.5 sm:py-3 pl-10 pr-4 text-xs sm:text-sm font-bold focus:outline-none transition-all ${inputBg}`} placeholder="usuario@dominio.com" required />
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <label className={`text-[9.5px] sm:text-[10.5px] font-black uppercase ml-1 ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600'}`}>Contraseña</label>
                                <div className="relative group">
                                    <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-indigo-500 transition-colors" />
                                    <input type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} className={`w-full border rounded-xl py-2.5 sm:py-3 pl-10 pr-10 text-xs sm:text-sm font-bold focus:outline-none transition-all ${inputBg}`} placeholder="••••••••" required />
                                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-indigo-500 p-1">
                                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </button>
                                </div>
                            </div>
                            {error && (<div className="p-2.5 bg-rose-500/10 border border-rose-500/20 text-rose-500 text-[8px] sm:text-[9px] font-black text-center rounded-lg uppercase animate-in shake duration-300">{error}</div>)}
                            <button type="submit" disabled={isLoading} className="w-full bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 text-white font-black py-3 sm:py-4 rounded-xl shadow-lg transition-all active:scale-[0.98] disabled:opacity-50 uppercase text-[8px] sm:text-[9px] tracking-[0.2em] flex items-center justify-center">{isLoading ? <Loader2 size={16} className="animate-spin" /> : "Iniciar Sesión"}</button>
                        </form>
                        
                        <div className="flex items-center w-full gap-4 my-6">
                            <div className={`flex-1 h-[0.5px] ${theme === 'dark' ? 'bg-white/10' : 'bg-slate-200'}`}></div>
                            <span className={`text-[9px] font-medium text-slate-400 dark:text-slate-500`}>or</span>
                            <div className={`flex-1 h-[0.5px] ${theme === 'dark' ? 'bg-white/10' : 'bg-slate-200'}`}></div>
                        </div>

                        <div className="flex flex-col items-center w-full">
                          <div id="googleBtn" className="w-full flex justify-center min-h-[44px]"></div>
                        </div>
                    </div>
                ) : (
                    <div className="animate-in slide-in-from-right-8 duration-500">
                        <button onClick={() => { setStep('credentials'); setError(null); }} className={`flex items-center space-x-2 text-[8px] sm:text-[9px] font-black uppercase hover:text-indigo-500 transition-colors mb-4 ${textMuted}`}><ArrowLeft size={14} /><span>Volver</span></button>
                        <div className="text-center mb-4 sm:mb-6">
                            <div className="relative inline-block mb-3">
                                <div className="absolute inset-0 bg-emerald-500 rounded-full blur-lg opacity-20"></div>
                                <div className={`relative w-12 h-12 rounded-full flex items-center justify-center border ${theme === 'dark' ? 'bg-[#1a1c22] border-emerald-500/30' : 'bg-white border-emerald-100'}`}><ShieldCheck size={24} className="text-emerald-500" /></div>
                            </div>
                            <h2 className={`text-lg font-black uppercase tracking-tight ${textMain}`}>2FA ACTIVADO</h2>
                            <div className={`mt-2 text-[8px] font-black uppercase px-3 py-1 rounded-full inline-block ${timeLeft < 60 ? 'bg-rose-500/10 text-rose-500' : 'bg-indigo-500/10 text-indigo-500'}`}>Vence en: {formatTime(timeLeft)}</div>
                        </div>
                        <form onSubmit={handleVerifyOtp} className="space-y-4">
                            <div className="relative group">
                                <input type="text" value={otpInput} onChange={e => setOtpInput(e.target.value.toUpperCase())} className={`w-full border rounded-xl py-3 sm:py-4 px-4 text-center text-lg sm:text-xl font-black tracking-[0.4em] focus:outline-none uppercase font-mono transition-all ${inputBg}`} placeholder="---" maxLength={6} required />
                            </div>
                            {error && (<div className="p-2.5 bg-rose-500/10 border border-rose-500/20 text-rose-500 text-[8px] sm:text-[9px] font-black text-center rounded-lg uppercase">{error}</div>)}
                            <button type="submit" className="w-full bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white font-black py-3 sm:py-4 rounded-xl shadow-lg uppercase text-[8px] sm:text-[9px] tracking-[0.2em]">Verificar Código</button>
                        </form>
                    </div>
                )}
            </div>
        </div>
      </div>
    </div>
  );
};
export default Login;